// Investor UI keeps its current look unchanged.
//
// This file exists solely so the `DesignTokens` module alias (generated
// per-app by process_tsconfig.js) resolves for the non-admin build the same
// way the admin build resolves it to src/components/admin/DesignTokens.ts.
// Every value below reproduces exactly what the corresponding component
// already hardcoded, so the Investor UI is pixel-identical to before —
// only the admin build gets new values.
export const RADIUS = {
  propertyCard: '1.25rem',
  propertyCardMini: '0.75rem',
  section: '3rem',
  sectionChild: '1rem',
  modal: '2rem',
  button: '999px',
  cardContainer: '16px',
  sectionCard: '1rem',
  card: 12,
};

export const SHADOW = {
  cardContainerHover: '0 8px 20px rgba(0,0,0,0.08)',
  sectionCard: '0 2px 8px rgba(0,0,0,0.05)',
  card: '0px 2px 4px rgba(0,0,0,0.1)',
};

export const SPACING = {
  sectionPaddingX: '1.875rem',
  sectionPaddingY: '1.875rem',
};

// Investor UI keeps the existing padding-top percentage hack (see
// PropertyCardImageSection.tsx) rather than switching to `aspect-ratio`, so
// this is unused there, but kept for type parity with the admin tokens.
export const IMAGE_ASPECT_RATIO = undefined;

// Reproduces PropertyCardImageSection's existing flex-fill-when-no-description
// behavior exactly.
export const IMAGE_FILLS_EMPTY_CARD = true;
export const FLUSH_IMAGE_ONLY_CARD = false;

// An image card with no title drops its empty title bar entirely.
export const HIDE_EMPTY_CARD_TITLE_ROW = true;

// Reproduces StyledHeadingBase's existing hardcoded padding exactly.
export const HEADING_SPACING = {
  paddingTop: '0.625rem',
  paddingTopFull: '2.5rem',
  paddingBottom: '1.875rem',
  paddingBottomReduced: '0.5rem',
};

// Reproduces HEADING_VARIANT_STYLE.standard's existing hardcoded 2.25rem.
export const HEADING_FONT_SIZE_STANDARD = '2.25rem';

// Reproduces PropertyCardGroup's existing hardcoded `pb: 0` exactly.
export const DESCRIPTION_BOTTOM_PADDING = 0;

// No override: the section title keeps StyledHeading's own bottom padding.
export const TITLE_BOTTOM_PADDING: string | undefined = undefined;

// Reproduces PropertyCardHeader's existing hardcoded pt: 2 / 1.5 (mini).
export const CARD_HEADER_PADDING_TOP = { default: 2, mini: 1.5 };

// Reproduces PropertyCardHeader's existing hardcoded pb: 1.5 / 1 (mini).
export const CARD_HEADER_PADDING_BOTTOM = { default: 1.5, mini: 1 };

// Reproduces PropertyCardFooter's existing lack of top padding exactly.
export const CARD_FOOTER_PADDING_TOP = { default: 0, mini: 0 };

// Reproduces PropertyCardFooter's existing hardcoded pb: 1 exactly.
export const CARD_FOOTER_PADDING_BOTTOM = { default: 1, mini: 1 };

// Reproduces PropertyCardBodyContent's existing hardcoded pb: 2 / 1.5 (mini).
export const CARD_BODY_PADDING_BOTTOM = { default: 2, mini: 1.5 };

// Reproduces descriptionClampSx's existing hardcoded mb: 2 / 1 (mini).
export const CARD_BODY_DESCRIPTION_MARGIN_BOTTOM = { default: 2, mini: 1 };

// Reproduces StyledButton's existing hardcoded 1.125rem exactly.
export const BUTTON_FONT_SIZE = '1.125rem';

// Investor UI keeps the existing full-bleed image (no inset, no separate
// image radius). Keep the units: a bare 0 inside PropertyCardImageSection's calc() is invalid.
export const IMAGE_INSET = { size: '0px', sizeMini: '0px', top: '0px', topMini: '0px' };
export const IMAGE_RADIUS = 0;

// Investor UI keeps its natural (unequal) card heights.
export const PROPERTY_CARD_EQUAL_HEIGHT = false;

// Investor UI keeps the existing flex-wrap card row.
export const CARD_GRID_LAYOUT = false;

// Investor UI keeps cards packed from the left in every row.
export const FLUSH_FULL_CARD_ROWS = false;

// Reproduces StyledCardLink's existing plain-underline-link look exactly.
// borderBottom is set per-instance in StyledCardLink.tsx since it needs
// Palette.default.text, which isn't available in this plain data file.
export const CARD_LINK_BUTTON = {
  background: 'transparent',
  color: undefined, // Wrapper/Text already default to Palette.default.text
  borderRadius: 0,
  padding: '0.5rem 0',
  borderBottom: undefined,
  width: '100%',
  minWidth: undefined,
  height: undefined,
  fontFamily: 'Manrope, sans-serif',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  fontWeight: 600,
  justifyContentNoArrow: 'space-between' as const,
  textNudge: true,
  hoverBackground: undefined,
};

// Reproduces PropertyCardFooter's existing `flex: 1` wrapper exactly.
export const CARD_LINK_STRETCH = true;

// Investor UI keeps content flush against the header (no extra push-down).
export const CONTENT_TOP_PADDING_EXTRA = '0px';

// Investor UI keeps MUI's default theme — no component overrides.
export const MUI_COMPONENT_OVERRIDES = {};

// Reproduces StyledFieldRow's existing hardcoded 1rem exactly.
export const FORM_ROW_MARGIN_BOTTOM = '1rem';

// Reproduces GenericChatWindow's previous hardcoded values exactly — Investor
// UI's Vera chat is unchanged.
export const CHAT_WINDOW = {
  messageAreaMinHeight: { xs: '60vh', md: '300px' },
  messageAreaMaxHeight: { xs: '60vh', md: '300px' },
  messageAreaRadius: '4px',
  userBubbleRadius: '18px 18px 4px 18px',
  modelBubbleRadius: '18px 18px 18px 4px',
  modelBubbleBorderWidth: '0px',
  showAssistantAvatar: false,
  avatarSize: 0,
  avatarImageSrc: '',
  useBrandedColors: false,
  bubbleElevation: 1,
  timestampFontSize: '0.65rem',
  attachInsideInput: false,
  bubblePaddingY: 1.25,
  bubblePaddingX: 1.25,
  useCircularSendButton: false,
  sendButtonSize: 40,
  showTimestamps: true,
  composerInputRadius: '8px',
  messageGap: 1.25,
  tightenMarkdownSpacing: false,
  // Investor UI's Vera chat keeps the browser-inherited size (unset) exactly
  // as before.
  bubbleFontSize: undefined,
  bubbleLineHeight: undefined,
};

// StyledModal's close (X) button — reproduces the component's previous
// hardcoded styling exactly, so the Investor UI's Settings modals are
// pixel-identical to before. Only the admin build gets the new circular,
// Palette-driven look (see admin/DesignTokens.ts).
export const MODAL_CLOSE_BUTTON = {
  background: '#ffffff',
  border: '2px solid #e0e0e0',
  borderRadius: '50%',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  color: undefined,
  hoverBackground: '#f5f5f5',
  hoverBorder: '#d0d0d0',
};
